import {
    ChangeDetectorRef,
    Component, computed,
    EventEmitter, inject,
    Input,
    OnChanges,
    OnDestroy, OnInit,
    Output, signal,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {Const, ResourceType, WizardApplicationType} from '../../../core/helpers/Const';
import {MenuComponent} from '@fundamental-ngx/core';
import {Utils} from 'src/app/core/helpers/Utils';
import {Subscription} from "rxjs";
import {RuleModel} from "../../../core/models/misc/rule.model";
import {RTDModel} from "../../../core/models/resources/rtd.model";
import {
    OrganizationHierarchyStoreService
} from "../../../service/store/organization-hierarchy-store/organization-hierarchy-store.service";
import {Location} from "@angular/common";

const APP_SUITE_DISABLED_TOOLTIP = $localize`:XTOL|Tooltip for app disabled@@shared.table-actions.unifiedSuiteWizardAppDisabledTooltip:To access the wizard, you must be listed as a service provider in the root of your provider folder. Please ask one of the service providers to include you in the \'services.serviceprovider\' Binding resource.`;
const SUITE_ALREADY_EXISTS_TOOLTIP = $localize`:XTOL|Tooltip for suite disabled@@shared.table-actions.unifiedSuiteWizardSuiteDisabledTooltip:You cannot set up a product blueprint in a folder that already has a product blueprint configuration`;

@Component({
    selector: 'app-table-actions',
    templateUrl: './table-actions.component.html',
    styleUrls: ['./table-actions.component.scss'],
    standalone: false
})
export class TableActionsComponent implements OnDestroy, OnChanges, OnInit {
    private readonly location = inject(Location);
    private readonly organizationHierarchyStoreService: OrganizationHierarchyStoreService = inject(OrganizationHierarchyStoreService);
    private readonly _cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    private unregisterUrlCallback?: VoidFunction;

    Utils = Utils;
    Const = Const;
    ResourceType = ResourceType;
    WizardApplicationType = WizardApplicationType;

    REQUIRED_APPLICATION_RESOURCES = [ResourceType.RTD,
        ResourceType.SERVICE_PUBLISH_CONFIGURATION,
        ResourceType.SERVICE_METADATA,
        ResourceType.MANAGED_SERVICE];

    @ViewChild('menu')
        menu: MenuComponent;
    @ViewChild('quickStartMenu')
        quickStartMenu: MenuComponent;
    searchTerm: string;

    @Output() doSearch = new EventEmitter<string>();

    @Output() resetFilter = new EventEmitter<void>();

    @Output() createItem = new EventEmitter<any>();
    @Output() createApplication = new EventEmitter<WizardApplicationType>();

    @Output() createGenericResource = new EventEmitter<ResourceType>();
    @Output() createServiceInstance = new EventEmitter<any>();
    @Output() advancedSearchToggled = new EventEmitter<boolean>();
    @Input() isLiveSearch = true;
    @Input() showCreateMenu = false;
    @Input() showCreateYAML = false;
    @Input() showCreateFolder = false;
    @Input() showCreateResourceGroup = false;
    @Input() showCreateManagedService = false;
    @Input() showCreateServiceInstance = false;
    @Input() systemResourcesByGroup: any[];
    @Input() applicationResources: any[] = [];
    @Input() showCreateApplication = false;
    @Input() currentPath: string;
    @Input() currentOrg: string;
    @Input() resourceType: ResourceType;
    @Input() advancedSearchOpen = false;
    @Input() advancedSearchReady = false;

    @Input() loadingFinished = false;

    subSystemResourcesByGroup: any[];

    isCreateApplicationDisabled = signal(false);
    isCreateSuiteDisabled = signal(false);
    createSuiteDisabledTooltip = computed(() => {
        if (this.isCreateApplicationDisabled()) {
            return APP_SUITE_DISABLED_TOOLTIP;
        }
        if (this.isCreateSuiteDisabled()) {
            return SUITE_ALREADY_EXISTS_TOOLTIP;
        }
        return '';
    });
    createAppDisabledTooltip = computed(() => {
        if (this.isCreateApplicationDisabled()) {
            return APP_SUITE_DISABLED_TOOLTIP;
        }
        return '';
    });

    subscriptions: Subscription[] = [];

    folderGlyph = Utils.getFontAwesomeResourceGlyph(ResourceType.FOLDER);
    resourceGroupGlyph = Utils.getFontAwesomeResourceGlyph(ResourceType.RESOURCE_GROUP);

    constructor() {
    }

    ngOnInit(): void {
        this.unregisterUrlCallback = this.location.onUrlChange(() => {
            this.onSearch('');
        });
    }


    ngOnChanges(changes: SimpleChanges) {
        if (changes['systemResourcesByGroup']?.currentValue) {
            this.showCreateFolder = this.systemResourcesByGroup.some((r) => ResourceType.FOLDER === r.type);
            this.showCreateResourceGroup = this.systemResourcesByGroup.some((r) => ResourceType.RESOURCE_GROUP === r.type);
            this.showCreateMenu = this.systemResourcesByGroup.length > 0 || this.showCreateServiceInstance;
            this.subSystemResourcesByGroup =
                this.systemResourcesByGroup.filter((r) => ![ResourceType.FOLDER, ResourceType.RESOURCE_GROUP].includes(r.type));
            if (Utils.hasFeatureFlag(Const.FEATURE_ENTITLEMENT_POLICIES)) {
                this.subSystemResourcesByGroup =
                    this.subSystemResourcesByGroup.filter((r) => r.type !== ResourceType.ASSIGNMENT);
            }
        }

        if (changes['applicationResources']?.currentValue) {
            const applicationResources = changes['applicationResources']?.currentValue;
            const applicationResourcesTypes = applicationResources.filter((item: RTDModel) => item.rules.some((rule: RuleModel) => rule.action === 'create' || rule.action === '*')).map((item: RTDModel) => item.type);

            const hasPermissions = this.REQUIRED_APPLICATION_RESOURCES.every((item: string) => applicationResourcesTypes.includes(item));
            this.isCreateApplicationDisabled.set(!hasPermissions);

            if (this.isCreateApplicationDisabled() || !this.currentPath) {
                return;
            }

            const resourcePaths = Utils.mapPathToResourcePaths(this.currentOrg, this.currentPath);
            const promises = this.organizationHierarchyStoreService.fetchContainersByPaths(this.resourceType, this.currentOrg, resourcePaths);
            Promise.all(promises).then((results) => {
                const isCreateSuiteDisabled = results.some(item => {
                    const productSuiteLabel = item.labels?.["usrv.resources.api.sap/product-suite-folder"];
                    return !!productSuiteLabel;
                });
                this.isCreateSuiteDisabled.set(!!isCreateSuiteDisabled);
            });
        }
        this._cdRef.detectChanges();
    }


    onCreateGenericResource() {
        this.createGenericResource.emit();
        this.menu.close();
    }

    onCreateItem(resourceType: ResourceType) {
        this.createItem.emit(resourceType);
        this.menu.close();
    }

    onCreateApplication(applicationType: WizardApplicationType) {
        this.createApplication.emit(applicationType);
        this.menu?.close();
        this.quickStartMenu?.close();
        return false;
    }

    onCreateServiceInstance(resource?: any) {
        this.createServiceInstance.emit(resource);
        this.menu.close();
    }

    onSearch(searchTerm: string) {
        this.searchTerm = searchTerm;
        this.doSearch.emit(searchTerm);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
        this.unregisterUrlCallback?.();
    }

    openAdvancedSearch() {
        this.advancedSearchOpen = !this.advancedSearchOpen;
        this.advancedSearchToggled.emit(this.advancedSearchOpen);
    }
}
